import { CustomModal, Loader } from 'components';
import DateUtils from 'utils/dateUtils';
import { WebviewFunctions } from 'constants/common';
import { NotifierTypes } from 'constants/notifierConstants';
import {
  useLazyGetMerchantTeamsQuery,
  useLazyGetPaymentSystemConfigQuery,
  useLazyGetTeamBankAccountQuery
} from 'containers/app/api';
import { selectCurrentTeam, selectPaymentSystemConfig, showNotifier } from 'containers/app/appSlice';
import { selectAuthState } from 'containers/auth/authSlice';
import { InternalBankStatuses, PaymentSystem } from 'containers/payouts/constants';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, useEffect, useState } from 'react';
import { LocalStorage } from 'services/storage';
import { VoidFn } from 'types/baseTypes';
import VerifyingState from './VerifyingState';

interface StripeFCModalProps {
  onClose: VoidFn;
  showSuccessToast?: boolean;
}

enum PAGE_STATES {
  IFRAME,
  VERIFYING
}

const StripeFCModal: FC<StripeFCModalProps> = ({ onClose, showSuccessToast = true }) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { paymentSystem } = useAppSelector(selectPaymentSystemConfig);
  const { idToken, refreshToken } = useAppSelector(selectAuthState) ?? {};
  const [getTeamBankAccountQuery] = useLazyGetTeamBankAccountQuery();
  const [getPaymentSystemConfigQuery] = useLazyGetPaymentSystemConfigQuery();
  const [getMerchantTeamsQuery, { isSuccess: merchantTeamsSuccess, isFetching: merchantTeamsFetching }] =
    useLazyGetMerchantTeamsQuery();
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState<PAGE_STATES>(PAGE_STATES.IFRAME);

  const iframeUrl = useState(() => {
    const baseUrl = process.env.REACT_APP_ONBOARDING_URL;
    const currentIdToken = idToken || LocalStorage.get('idToken');
    const currentRefreshToken = refreshToken || LocalStorage.get('refreshToken');
    const teamId = currentTeam?.id;
    const acceptedAt = encodeURIComponent(DateUtils.getCurrentUTCTimestamp());
    const sys = paymentSystem ?? PaymentSystem.CA;
    return (
      `${baseUrl}/#/ca-connect-external-account?isIframe=true&idToken=${currentIdToken}` +
      `&refreshToken=${currentRefreshToken}&teamId=${teamId}&paymentSystem=${sys}&acceptedAt=${acceptedAt}`
    );
  })[0];

  const handleIframeOnLoad = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    if (merchantTeamsSuccess && !merchantTeamsFetching) {
      if (currentTeam.internalBankingStatus === InternalBankStatuses.FINANCIAL_DATA_SUBMITTED) {
        setPage(PAGE_STATES.VERIFYING);
        setIsLoading(false);
      } else {
        handleOnDone();
      }
    }
  }, [merchantTeamsFetching, merchantTeamsSuccess]);

  useEffect(() => {
    const handleReadMessage = async event => {
      if (event.data === WebviewFunctions.WEBVIEW_LOADED) {
        setIsLoading(false);
      }
      if (event.data === WebviewFunctions.CLOSE_WEBVIEW) {
        onClose();
      }
      if (event.data === WebviewFunctions.UPDATE_BANK_STATUS) {
        setIsLoading(true);
        setPage(null);
        getMerchantTeamsQuery();
      }
    };

    // Add the event listener.
    window.addEventListener('message', handleReadMessage, false);
    return () => {
      window.removeEventListener('message', handleReadMessage, false);
    };
  }, []);

  const handleOnDone = async () => {
    getPaymentSystemConfigQuery(currentTeam.id);
    await getTeamBankAccountQuery(currentTeam.id);
    onClose();
    if (currentTeam.internalBankingStatus === InternalBankStatuses.FINANCIAL_DATA_VERIFIED) {
      if (showSuccessToast) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'Bank account connected successfully'
            },
            type: NotifierTypes.SUCCESS,
            showClose: true
          })
        );
      }
    }
  };

  return (
    <CustomModal open width="md:min-w-[400px]" height="min-h-[623px]" className="relative">
      {isLoading && (
        <>
          <div className="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2">
            <Loader />
          </div>
        </>
      )}
      {page === PAGE_STATES.IFRAME && <iframe src={iframeUrl} width={400} height={623} onLoad={handleIframeOnLoad} />}

      {page === PAGE_STATES.VERIFYING && (
        <>
          <VerifyingState onDone={handleOnDone} className="py-20" />
        </>
      )}
    </CustomModal>
  );
};

export default StripeFCModal;
